import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import { uniq } from 'lodash';
import { useRouter } from 'next/router';
import { FC, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import Slider from 'react-slick';
import Lightbox from 'react-spring-lightbox';
import { toast } from 'react-toastify';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import Colors from '~/constants/colors';
import { ADD_PRODUCT_TO_WISH_LIST, REMOVE_PRODUCT_FROM_WISH_LIST } from '~/data/home';
import { Product, ProductMedia, ProductVariant } from '~/services/product';
import productAtom from '~/services/product/product.atom';
import { UserAtom } from '~/services/user';
import { WishListAtom } from '~/services/wishlist';
import Icon from '../Icon';
import styles from './ProductDetailImage.module.scss';
import { trackingWishList } from '~/services/tracking';
import SocialShare from '~/sections/SocialShare';
import RewardIcon from '~/assets/svgs/reward.svg';
import Text from '../Text';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css/navigation';

import { Navigation, Pagination, Thumbs, FreeMode } from 'swiper/modules';
import TooltipTwo from '../TooltipTwo';

interface ProductDetailImageProps {
    isBestSeller: boolean;
    product: Partial<Product>;
    variant: ProductVariant;
}

const ProductDetailImage: FC<ProductDetailImageProps> = ({ isBestSeller, product, variant }) => {
    const router = useRouter();
    const currentUser = useRecoilValue(UserAtom.currentUser);
    const sliderRef = useRef<Slider>(null);
    const [openLightBox, setOpenLightBox] = useState(false);
    const [loadingFavoriteWithoutWishlistId, setLoadingFavoriteWithoutWishlistId] = useState(false);
    const [currentLightBoxIndex, setCurrentLightBoxIndex] = useState(0);

    const { hoverSwatchOptions } = useRecoilValue(productAtom.Product);
    const setWishList = useSetRecoilState(WishListAtom.wishlist);
    const { wishlistItems, id } = useRecoilValue(WishListAtom.WishlistSelector);
    const productWishlist = wishlistItems?.find((wishlistItem) => wishlistItem?.product?.sku === product?.sku);

    const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);

    const [addProductToWishlist, { loading: loadingAddWish }] = useMutation(ADD_PRODUCT_TO_WISH_LIST, {
        onError: ({ message }) => toast.error(message)
    });

    const [removeProductWishlist, { loading: loadingremove }] = useMutation(REMOVE_PRODUCT_FROM_WISH_LIST, {
        onError: ({ message }) => toast.error(message)
    });
    const swatch_attribute = product.swatch_attribute;

    const images: any[] = useMemo(() => {
        if (!!hoverSwatchOptions?.thumbnail) {
            return [hoverSwatchOptions?.thumbnail].concat(product?.images || [])?.filter((item) => !!item);
        }
        if (variant?.product?.thumbnail?.url && swatch_attribute) {
            const listVariants = product.variants?.filter(
                (v) =>
                    v.attributes.findIndex(
                        (attr: any) =>
                            attr.code === swatch_attribute &&
                            variant.attributes.findIndex((vAttr: any) => vAttr.value_index === attr.value_index) > -1
                    ) > -1
            );
            const swatchThumbnail = listVariants?.[0]?.product?.thumbnail?.url;
            return uniq([swatchThumbnail].concat(product?.images || [])?.filter((item) => !!item)) || [];
        }
        const thumbnail = product?.media_gallery?.find((item: ProductMedia) => item.is_thumbnail)?.url ?? "";
        return uniq([thumbnail].concat(product?.images?.filter(item => item !== thumbnail) || []))?.filter((item) => !!item) || [];
    }, [product.images, product.image, variant?.product?.thumbnail?.url, hoverSwatchOptions]);

    useEffect(() => {
        if (images?.[0]) {
            sliderRef.current?.slickGoTo?.(0);
        }
    }, [images]);

    const handleFavorite = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();

        if (!currentUser?.email) {
            router.push('/sign-in');
            return;
        }

        // setIsFavorite(!isFavorite);
        id && (productWishlist?.id
            ? removeProductWishlist({
                variables: {
                    wishlistId: id,
                    id: productWishlist?.id
                },
                onCompleted(data) {
                    setWishList((prev) => ({
                        ...prev,
                        ...data?.removeProductsFromWishlist?.wishlist
                    }));
                }
            })
            : addProductToWishlist({
                variables: {
                    wishlistId: id,
                    sku: product?.sku
                },
                onCompleted(data, clientOptions) {
                    trackingWishList({
                        product: product as Partial<Product>
                    });

                    setWishList((prev) => ({
                        ...prev,
                        ...data?.addProductsToWishlist?.wishlist
                    }));
                }
            }));

        if (!id && productWishlist?.id) {

            setLoadingFavoriteWithoutWishlistId(true);
            const currentAddLocalToWishList = localStorage.getItem(`addLocalToWishList-${product.sku}`);
            currentAddLocalToWishList && localStorage.removeItem(`addLocalToWishList-${product.sku}`);
            !currentAddLocalToWishList && localStorage.setItem(`removeLocalFromWishList-${productWishlist.id}`, productWishlist.id);
        } else if (!id && product?.sku) {
            setLoadingFavoriteWithoutWishlistId(true);
            localStorage.setItem(`addLocalToWishList-${product.sku}`, product.sku);
        }
    };

    if (!images.length) {
        return (
            <div className={styles.product_detail_image}>
                <Skeleton className="relative w-full aspect-square"></Skeleton>
                <div className="flex gap-2">
                    {Array.from({ length: 4 }, (_item, index: number) => {
                        return (
                            <Skeleton
                                key={index}
                                width={120}
                                height={120}
                                className={classNames(styles.image_item, 'aspect-square')}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    useEffect(() => {
        const currentAddLocalToWishList = Number(localStorage.getItem(`addLocalToWishList-${product.sku}`));
        const currentRemoveLocalFromWishList = productWishlist?.id && localStorage.getItem(`removeLocalFromWishList-${productWishlist.id}`);

        if (id && !currentRemoveLocalFromWishList && !currentRemoveLocalFromWishList) {
            setLoadingFavoriteWithoutWishlistId(false);
        }

        if (id && currentAddLocalToWishList) {
            addProductToWishlist({
                variables: {
                    wishlistId: id,
                    sku: currentAddLocalToWishList
                },
                onCompleted(data) {
                    trackingWishList({
                        product: product as Partial<Product>
                    });
                    localStorage.removeItem(`addLocalToWishList-${product.sku}`)
                    setLoadingFavoriteWithoutWishlistId(false);
                    setWishList((prev) => ({
                        ...prev,
                        ...data?.addProductsToWishlist?.wishlist
                    }));
                }
            })
        }

        if (id && currentRemoveLocalFromWishList) {
            removeProductWishlist({
                variables: {
                    wishlistId: id,
                    id: currentRemoveLocalFromWishList
                },
                onCompleted(data) {
                    localStorage.removeItem(`removeLocalFromWishList-${productWishlist.id}`);
                    setLoadingFavoriteWithoutWishlistId(false);
                    setWishList((prev) => ({
                        ...prev,
                        ...data?.removeProductsFromWishlist?.wishlist
                    }));
                }
            })
        }
    }, [id, addProductToWishlist, removeProductWishlist, productWishlist?.id, product.sku])

    return (
        <div className={styles.product_detail_image}>
            <div className="relative w-full aspect-square">
                <Lightbox
                    isOpen={openLightBox}
                    currentIndex={currentLightBoxIndex}
                    images={images.map((img) => ({
                        src: img,
                        loading: 'lazy',
                        alt: 'image'
                    }))}
                    onNext={() => setCurrentLightBoxIndex(currentLightBoxIndex + 1)}
                    onPrev={() => setCurrentLightBoxIndex(currentLightBoxIndex - 1)}
                    renderHeader={() => (
                        <div className="lightbox-image-pager flex justify-end p-4">
                            <div
                                className="w-10 h-10 cursor-pointer flex items-center justify-center"
                                onClick={() => setOpenLightBox(false)}
                            >
                                <Icon name="close" color="white" size={32} />
                            </div>
                        </div>
                    )}
                    renderFooter={() => (
                        <div
                            className={classNames(
                                styles.lighbox_list,
                                'flex justify-center gap-3 py-6 lightbox-image-pager'
                            )}
                        >
                            {images.map((img, index) => (
                                <div
                                    key={index}
                                    className={classNames(styles.lightbox_image, 'cursor-pointer', {
                                        [styles.selected]: currentLightBoxIndex === index
                                    })}
                                    onClick={() => {
                                        setCurrentLightBoxIndex(index);
                                    }}
                                >
                                    <img src={img} alt={img} width={80} height={80} />
                                </div>
                            ))}
                        </div>
                    )}
                />
                <Swiper
                    navigation={true}
                    modules={[FreeMode, Navigation, Thumbs, Pagination]}
                    thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                    pagination={{
                        clickable: true
                    }}
                    breakpoints={{
                        1024: {
                            pagination: false
                        }
                    }}
                    onClick={(swiper, event) => {
                        setOpenLightBox(true);
                        setCurrentLightBoxIndex(swiper.clickedIndex);
                    }}
                    className="mySwiperMain"
                >
                    {[...images]?.map((img) => {
                        return (
                            <SwiperSlide>
                                <img key={img} src={img} alt={img} className={classNames(styles.image, 'w-full')} />
                            </SwiperSlide>
                        );
                    })}
                </Swiper>

                <div>
                    <div className={classNames(styles.favourite, 'flex flex-col gap-2')}>
                        <div>
                            {loadingAddWish || loadingremove || loadingFavoriteWithoutWishlistId ? (
                                <span className="flex">
                                    <Icon
                                        name="status"
                                        color={Colors.GREY_300}
                                        className="animate-spin w-10 h-10 bg-white shadow rounded-full cursor-pointer flex items-center justify-center"
                                    />
                                </span>
                            ) : (
                                <div onClick={handleFavorite}>
                                    <Icon
                                        className="w-10 h-10 bg-white shadow rounded-full cursor-pointer flex items-center justify-center"
                                        name={!!productWishlist?.id ? 'heart-fill' : 'heart'}
                                        color={!!productWishlist?.id ? Colors.ACCENT_RED : Colors.FAVORITE_COLOR}
                                    />
                                </div>
                            )}
                        </div>
                        <SocialShare title={product?.name || ''} image={product?.image?.url || ''} />
                    </div>
                    {isBestSeller && (
                        <div className="absolute right-1 top-4 z-[2]">
                            <TooltipTwo
                                id='best-seller'
                                title={<Text className="w-[200px]">
                                    This item has had a high sales volume over the past 6 months.
                                </Text>}
                            >
                                <div className="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-sm font-medium shadow">
                                    <RewardIcon width={24} height={24} color={Colors.PRIMARY} />
                                    <Text color={Colors.PRIMARY}>Bestseller</Text>
                                </div>
                            </TooltipTwo>
                        </div>
                    )}
                </div>
                <div className="hidden lg:block">
                    <Swiper
                        onSwiper={setThumbsSwiper}
                        // pagination={{
                        //     clickable: true
                        // }}
                        modules={[Navigation, FreeMode, Thumbs]}
                        freeMode={true}
                        watchSlidesProgress={true}
                        slidesPerView={10}
                        className={'mySwiper'}
                    >
                        {[...images]?.map((img) => {
                            return (
                                <SwiperSlide>
                                    <img key={img} src={img} alt={img} className={classNames(styles.image, 'max-w-[100px]')} />
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailImage;
